/**
 * Zustand is normally used for global state, or at least state that needs to be deeply shared with component tree.
 * However, here we're using it as local state manager, for the non-trivial persistance requirements.
 */

import { functionalUpdate, type TableState, type TableOptions } from "@tanstack/react-table";
import { useState } from "react";
import { createStore } from "zustand";
import { persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { useStore } from "zustand/react";
import createDeepMerge from "@fastify/deepmerge";
import { devtools } from "zustand/middleware";

const deepMerge = createDeepMerge({
  all: true,
  mergeArray: (options) => (target, source) => options.clone(source),
});

interface PersistedTableStateOptions {
  persistKey: string;
  initialState?: Partial<ManagedTableState>;
  skipPersisting?: (keyof ManagedTableState)[];
}

export type ManagedTableState = Omit<TableState, "columnSizingInfo">;

export type ManagedTableStateSetters = Required<
  Pick<TableOptions<unknown>, `on${Capitalize<keyof ManagedTableState>}Change`>
>;

const defaults: ManagedTableState = {
  columnFilters: [],
  columnOrder: [],
  columnPinning: {},
  columnSizing: {},
  columnVisibility: {},
  expanded: {},
  globalFilter: "",
  grouping: [],
  pagination: { pageIndex: 0, pageSize: 10 },
  rowPinning: {},
  rowSelection: {},
  sorting: [],
};

const createTableStateStore = ({
  persistKey,
  initialState,
  skipPersisting = ["columnPinning", "expanded", "globalFilter", "rowPinning", "rowSelection"],
}: PersistedTableStateOptions) => {
  const initial = deepMerge(defaults, initialState) as ManagedTableState;
  return createStore<{
    state: ManagedTableState;
    initialState: ManagedTableState;
    setters: ManagedTableStateSetters;
  }>()(
    devtools(
      persist(
        immer((set) => ({
          state: initial,
          initialState: initial,
          setters: {
            onPaginationChange: (updaterOrValue) => {
              set(
                (state) => {
                  state.state.pagination = functionalUpdate(updaterOrValue, state.state.pagination);
                },
                undefined,
                "pagination",
              );
            },
            onColumnVisibilityChange: (updaterOrValue) => {
              set(
                (state) => {
                  state.state.columnVisibility = functionalUpdate(updaterOrValue, state.state.columnVisibility);
                },
                undefined,
                "columnVisibility",
              );
            },
            onColumnOrderChange: (updaterOrValue) => {
              set(
                (state) => {
                  state.state.columnOrder = functionalUpdate(updaterOrValue, state.state.columnOrder);
                },
                undefined,
                "columnOrder",
              );
            },
            onSortingChange: (updaterOrValue) => {
              set(
                (state) => {
                  state.state.sorting = functionalUpdate(updaterOrValue, state.state.sorting);
                  state.state.pagination.pageIndex = 0;
                },
                undefined,
                "sorting",
              );
            },
            onColumnFiltersChange: (updaterOrValue) => {
              set(
                (state) => {
                  state.state.columnFilters = functionalUpdate(updaterOrValue, state.state.columnFilters);
                  state.state.pagination.pageIndex = 0;
                },
                undefined,
                "columnFilters",
              );
            },
            onColumnPinningChange: (updaterOrValue) => {
              set(
                (state) => {
                  state.state.columnPinning = functionalUpdate(updaterOrValue, state.state.columnPinning);
                },
                undefined,
                "columnPinning",
              );
            },
            onColumnSizingChange: (updaterOrValue) => {
              set(
                (state) => {
                  state.state.columnSizing = functionalUpdate(updaterOrValue, state.state.columnSizing);
                },
                undefined,
                "columnSizing",
              );
            },
            onRowSelectionChange: (updaterOrValue) => {
              set(
                (state) => {
                  state.state.rowSelection = functionalUpdate(updaterOrValue, state.state.rowSelection);
                },
                undefined,
                "rowSelection",
              );
            },
            onExpandedChange: (updaterOrValue) => {
              set(
                (state) => {
                  state.state.expanded = functionalUpdate(updaterOrValue, state.state.expanded);
                },
                undefined,
                "expanded",
              );
            },
            onGlobalFilterChange: (updaterOrValue) => {
              set(
                (state) => {
                  state.state.globalFilter = functionalUpdate(updaterOrValue, state.state.globalFilter);
                },
                undefined,
                "globalFilter",
              );
            },
            onGroupingChange: (updaterOrValue) => {
              set(
                (state) => {
                  state.state.grouping = functionalUpdate(updaterOrValue, state.state.grouping);
                },
                undefined,
                "grouping",
              );
            },
            onRowPinningChange: (updaterOrValue) => {
              set(
                (state) => {
                  state.state.rowPinning = functionalUpdate(updaterOrValue, state.state.rowPinning);
                },
                undefined,
                "rowPinning",
              );
            },
          },
        })),
        {
          name: `tableState-${persistKey}`,
          version: 1,
          merge: (persisted, current) => deepMerge(current, persisted) as never,
          partialize({ state }) {
            const persistedState = omitSet(state, new Set(skipPersisting));
            // Keep the page size, not page index. Harcoded, don't see it ever useful to persist the index.
            if ("pagination" in persistedState) {
              persistedState.pagination = {
                pageSize: persistedState.pagination!.pageSize,
              } as ManagedTableState["pagination"];
            }
            return {
              state: persistedState,
            };
          },
        },
      ),
    ),
  );
};

export const usePersistedTableState = (options: PersistedTableStateOptions) => {
  const [store] = useState(() => createTableStateStore(options));
  return useStore(store);
};

function omitSet<T extends object>(obj: T, exclude: Set<keyof T>): Partial<T> {
  return Object.fromEntries(Object.entries(obj).filter(([key]) => !exclude.has(key as keyof T))) as Partial<T>;
}
